import type { ColumnDef } from '@tanstack/react-table';
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';
import {
	type MyCarriersDTO,
	useGetMyCarriersListSuspense,
} from '@uturn/api/v1';
import type { BadgeProps } from '@uturn/ui-kit';
import {
	Badge,
	Stars,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@uturn/ui-kit';
import { useTranslation } from 'react-i18next';

export const BlacklistedCarriersTable = () => {
	const { t } = useTranslation();

	const { data: carriers } = useGetMyCarriersListSuspense({
		query: {
			select: (resp) =>
				(resp.data ?? []).filter(
					(carrier) => (carrier.relationStatus ?? '').toLowerCase() === 'blacklisted',
				),
		},
	});

	const columns: ColumnDef<MyCarriersDTO>[] = [
		{
			header: t('general.carrier', 'Carrier').toString(),
			cell: (cell) => {
				const row = cell.row.original;
				return (
					<div className="flex flex-col items-start justify-start">
						<span className="font-medium">{row.carrierParty}</span>
						<span className="text-muted-foreground mt-1 text-xs">
							{row.carrierEmail}
						</span>
					</div>
				);
			},
		},
		{
			header: t('general.status', 'Status').toString(),
			cell: (cell) => {
				const row = cell.row.original;

				return <Badge variant="destructive">{row.relationStatus}</Badge>;
			},
		},
		{
			header: t('general.average-rating', 'Average rating').toString(),
			cell: (cell) => {
				const row = cell.row.original;

				return (
					<div className="flex flex-row items-center justify-start">
						<Stars
							rating={row.averageRating ? Math.round(row.averageRating) : 0}
						/>
						<span className="text-muted-foreground ml-2 mt-1 text-xs">
							{`(${t('general.reviews', {
								count: row.timesRated ?? 0,
							})})`}
						</span>
					</div>
				);
			},
		},
		{
			header: t('general.matches', 'Matches').toString(),
			accessorKey: 'totalMatches',
		},
	];

	const table = useReactTable({
		data: carriers,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-x-auto rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<TableHead key={header.id}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows?.length ? (
						table.getRowModel().rows.map((row) => (
							<TableRow
								key={row.id}
								data-state={row.getIsSelected() && 'selected'}
							>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center">
								{t('general.no-results', 'No results.')}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
};
